import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  PausedJobMoveDecision,
  usePausedJobMoveGate,
} from "@/contexts/PausedJobMoveGateContext";
import { ACTIVE_SCREENS_QUERY_KEY } from "@/hooks/queries/useGetActiveScreens";
import { candidaciesMinimalQueryKey } from "@/hooks/queries/useGetCandidaciesMinimal";
import { candidacyInfoQueryKey } from "@/hooks/queries/useGetCandidacyInfo";
import { guacHttpClient } from "@/lib/http";
import {
  ActiveScreeningPrompt,
  CandidacyInfoResponse,
  CandidacyStatus,
  HttpResponse,
  MinimalCandidacy,
  MyCandidacyResponse,
  UpdateCandidacyRequest,
  UpdateCandidacyResponse,
} from "@/types";

// Updates candidacy status from global screening modal
export function useUpdateScreeningPromptCandidacy() {
  const queryClient = useQueryClient();
  const pausedJobMoveGate = usePausedJobMoveGate();

  const mutation = useMutation({
    mutationFn: async ({
      companyId,
      jobId,
      candidacyId,
      updateCandidacyRequest,
    }: {
      companyId: string;
      jobId: string;
      candidacyId: string;
      updateCandidacyRequest: UpdateCandidacyRequest;
    }) => {
      const path = `companies/${companyId}/jobs/${jobId}/candidacies/${candidacyId}`;
      const res = await guacHttpClient.patch<
        UpdateCandidacyResponse,
        HttpResponse<UpdateCandidacyResponse>,
        UpdateCandidacyRequest
      >(path, updateCandidacyRequest);

      return res.data;
    },
    onSuccess: (_data, variables) => {
      const { companyId, jobId, candidacyId, updateCandidacyRequest } =
        variables;

      // Drop the acted screen from the active screens cache so it falls off screen
      queryClient.setQueryData(
        ACTIVE_SCREENS_QUERY_KEY,
        (
          cached?: ActiveScreeningPrompt[],
        ): ActiveScreeningPrompt[] | undefined =>
          cached?.filter((screen) => screen.candidacyId !== candidacyId),
      );

      const newStatus = updateCandidacyRequest.status;
      if (newStatus === undefined) return;

      const reason = updateCandidacyRequest.statusChangeReason ?? null;
      // viaScreened means the BE chains the prior status -> SCREENED -> newStatus,
      // so the new change's fromStatus is SCREENED.
      const viaScreened = updateCandidacyRequest.viaScreened ?? false;

      // Update the candidacy status in the job pipeline
      queryClient.setQueriesData(
        { queryKey: candidaciesMinimalQueryKey(companyId, jobId) },
        (cached?: MinimalCandidacy[]): MinimalCandidacy[] | undefined =>
          cached?.map((c) =>
            c.id === candidacyId
              ? {
                  ...c,
                  lastCandidacyStatusChange: {
                    createdAt: new Date().toISOString(),
                    fromStatus: viaScreened
                      ? CandidacyStatus.SCREENED
                      : c.lastCandidacyStatusChange.toStatus,
                    toStatus: newStatus,
                    statusChangeReason: reason,
                    byAgent: false,
                  },
                }
              : c,
          ),
      );

      // Update candidacy status in candidate sheet
      queryClient.setQueryData(
        candidacyInfoQueryKey(companyId, jobId, candidacyId),
        (cached?: CandidacyInfoResponse): CandidacyInfoResponse | undefined =>
          cached
            ? {
                ...cached,
                status: newStatus,
                lastStatusChangeByAgent: false,
                lastCandidacyStatusChange: {
                  fromStatus: viaScreened
                    ? CandidacyStatus.SCREENED
                    : cached.status,
                  toStatus: newStatus,
                  statusChangeReason: reason,
                  byAgent: false,
                },
              }
            : cached,
      );

      // Update candidacy status in aviary
      queryClient.setQueriesData(
        { queryKey: ["my-candidacies"] },
        (cached?: MyCandidacyResponse[]): MyCandidacyResponse[] | undefined =>
          cached?.map((row) =>
            row.candidacyId === candidacyId
              ? { ...row, status: newStatus }
              : row,
          ),
      );

      // Just require dashboard refetch, too complicated for different cases haha
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  // The screening card moves candidates to Screened / To Review, both inside
  // the range the parked-job gate covers, so it asks the same question the
  // pipeline surfaces do. Mirrors useUpdateCandidacy.
  const mutate: typeof mutation.mutate = (variables, options) => {
    if (!pausedJobMoveGate) {
      mutation.mutate(variables, options);
      return;
    }

    void pausedJobMoveGate
      .requestDecision({
        candidacyId: variables.candidacyId,
        fromStatus: variables.updateCandidacyRequest.fromStatus,
        jobId: variables.jobId,
        toStatus: variables.updateCandidacyRequest.status,
      })
      .then((decision) => {
        // The gate has already marked the job active if that was chosen.
        if (decision === PausedJobMoveDecision.CANCEL) return;
        mutation.mutate(variables, options);
      });
  };

  return { ...mutation, mutate };
}
